import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  Table,
  TableHeader,
  TableRow,
  TableBody,
  TableCell,
  TableHead,
} from "../../@/components/ui/table";
import { MdModeEdit, MdOutlineOndemandVideo } from "react-icons/md";
import { FaTrashAlt } from "react-icons/fa";
import SkeletonTableLoading from "../../skeletonLoading/SkeletonTableLoading";
import { useToast } from "../../@/components/ui/use-toast";
import { useSelector } from "react-redux";
import useDebounce, {
  autoFormatNumber,
  formatDate,
  formatDateTime,
  IsServiceTypeVendor,
  tokenDecoder,
} from "../../lib/utils";
import { useNavigate } from "react-router-dom";
import { MoveUp, MoveDown, ArrowUpDown, Eye } from "lucide-react";
import { FiSearch } from "react-icons/fi";
import { useDispatch } from "react-redux";
import { IoMdShare } from "react-icons/io";
import { useDeleteVideoMutation, useGetVideosMutation } from "../../api/api";
import { Input } from "../../@/components/ui/input";
import SingleDatePicker from "../../components/SingleDatePicker";
import ShareButton from "../../components/ShareButton";
import Pagination from "../../components/Pagination";
import {
  DEEP_LINK_BASE_URL,
  FILE_TYPE_ID,
  VIDEO_STATUS,
} from "../../lib/constants";
import { setAppState } from "../../redux/slices/appState";
import { Button } from "../../@/components/ui/button";
import VideoPreviewModal from "../../components/Video/VideoPreviewModal";
import { VendorsListDropdown } from "../../components/Video/VendorsListDropdown";

declare module "@tanstack/table-core" {
  interface ColumnMeta<TData extends unknown, TValue> {
    isTooltip?: boolean;
  }
}

interface Data {
  id: number;
  title: string;
  filePath: string;
  createdAt: string;
  totalLikes: number;
  totalDislikes: number;
  totalViews: number;
  videoStatus: string;
  statusId: number;
}

interface Props {}

const VideoListView: React.FC<Props> = ({}) => {
  const [selected, setSelected] = useState<any>({
    name: "All Videos",
    id: undefined,
  });
  const [searchTerm, setSearchTerm] = useState<string>("");
  const debouncedSearchQuery = useDebounce(searchTerm, 500);
  const token = useSelector((state: any) => state.auth.token);
  const decodedToken = tokenDecoder(token);
  const userRole = decodedToken.role;
  const vendorId = decodedToken.vendorId;
  const [selectedVendor, setSelectedVendor] = useState<string>("-1");
  const isMobile = window.innerWidth < 768;

  const options: {
    name: string;
    id: number | undefined;
    boolean: boolean | undefined;
  }[] = [
    { name: "All Videos", id: undefined, boolean: undefined },
    { name: "Published", id: 12, boolean: undefined },
    { name: "Draft", id: 11, boolean: undefined },
  ];

  const [data, setData] = useState<Data[]>([]);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [pageSize] = useState<number>(10);
  const [totalCount, setTotalCount] = useState<number>(0);
  const navigate = useNavigate();
  const { toast } = useToast();
  const [deletingVideoId, setDeletingVideoId] = useState<number | null>(null);
  const [sortingOrder, setSortingOrder] = useState<"asc" | "desc" | undefined>(
    "desc"
  );
  const [sortingColumn, setSortingColumn] = useState<string | undefined>(
    "createdAt"
  );
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedvideoId, setSelectedVideoId] = useState(0);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [selectedVideoPath, setSelectedVideoPath] = useState("");
  const [getVideos, { isLoading: getVideosLoader, error }] =
    useGetVideosMutation();
  const [deleteVideo, { isLoading: isDeleting, error: deleteError }] =
    useDeleteVideoMutation();
  const dispatch = useDispatch();

  const handleSorting = (column: string) => {
    if (sortingColumn === column) {
      setSortingOrder((prevOrder) => (prevOrder === "asc" ? "desc" : "asc"));
    } else {
      setSortingColumn(column);
      setSortingOrder("asc");
    }
    setPageNumber(1);
  };

  const fetchData = useCallback(async () => {
    try {
      const response: any = await getVideos({
        getVideosDto: {
          vendorId:
            userRole === "VENDOR"
              ? vendorId
              : selectedVendor === "-1"
              ? undefined
              : Number(selectedVendor),
          searchQuery:
            debouncedSearchQuery !== "" ? debouncedSearchQuery : undefined,
          pageNumber,
          pageSize,
          getTotalCount: true,
          // statusId: selected.id,
          sortingOrder,
          sortingColumn,
          startDate: startDate
            ? formatDateTime(startDate, "YYYY-MM-DDTHH:mm:ssZ")
            : undefined,
          endDate: endDate
            ? formatDateTime(
                endDate.setHours(23, 59, 59, 999),
                "YYYY-MM-DDTHH:mm:ssZ"
              )
            : undefined,
        },
      }).unwrap();

      setData(response?.data?.videos || []);
      setTotalCount(response?.data?.totalCount || 0);
    } catch (err) {
      console.error("Failed to get videos: ", err);
    }
  }, [
    getVideos,
    debouncedSearchQuery,
    selected,
    pageNumber,
    vendorId,
    sortingOrder,
    sortingColumn,
    startDate,
    endDate,
    selectedVendor,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData, pageNumber, selectedVendor]);

  const handlePageChange = (newPageNumber: number) => {
    if (newPageNumber < 0 || newPageNumber > Math.ceil(totalCount / pageSize)) {
      return;
    }
    setPageNumber(newPageNumber);
  };

  // const handleWatchVideo = (videoId: number) => {
  //   const video = data.find((v) => v.id === videoId);

  //   if (video) {
  //     // setSelectedVideoPath(video.filePath);
  //     // setIsPreviewOpen(true);
  //   }
  //   // else {
  //   //   toast({
  //   //     title: "Error",
  //   //     description: "Could not load video preview. Please try again.",
  //   //   });
  //   // }
  // };

  const handleRemoveVideo = async (videoId: number, statusId: number) => {
    if (statusId === VIDEO_STATUS.VIDEO_IN_PROCESS) {
      toast({
        variant: "destructive",
        showIcon: true,
        title: "Error",
        description:
          "You cannot delete a video that is in process. Please wait.",
      });
      return;
    }
    setDeletingVideoId(videoId);
    try {
      await deleteVideo({ body: { id: videoId } }).unwrap();
      toast({
        variant: "success",
        showIcon: true,
        title: "Success",
        description: "Video deleted successfully",
      });
      setDeletingVideoId(null);
      fetchData();
    } catch (err: any) {
      console.error("Failed to delete the video: ", err?.data?.message);
      toast({
        variant: "destructive",
        title: "Error",
        description: `${err?.data?.message}`,
        showIcon: true,
      });
      setDeletingVideoId(null);
    }
  };

  const handleShareVideo = (videoId: number) => {
    setIsDialogOpen(true);
    setSelectedVideoId(videoId);
  };
  const handleEditvideo = (videoId: number) => {
    navigate(`/edit-video/${videoId}`);
  };

  const columns = useMemo<ColumnDef<Data>[]>(() => {
    const baseColumns: ColumnDef<Data>[] = [
      // {
      //   accessorKey: "videoPreview",
      //   header: ({ column }) => (
      //     <div
      //       onClick={() => handleSorting("videoPreview")}
      //       className="cursor-pointer flex gap-1 items-center "
      //     >
      //       Video Preview
      //       {/* {sortingColumn === "videoPreview" ? (
      //         sortingOrder === "asc" ? (
      //           <MoveUp size={14} />
      //         ) : (
      //           <MoveDown size={14} />
      //         )
      //       ) : (
      //         <ArrowUpDown size={14} />
      //       )} */}
      //     </div>
      //   ),
      //   size: 100,
      //   cell: ({ row }) => (
      //     <div className="flex">
      //       <video
      //         src={row.original.filePath}
      //         className="w-16 h-10 object-cover rounded-md"
      //         preload="metadata"
      //         onLoadedMetadata={(e) => {
      //           e.currentTarget.currentTime = 0.1;
      //         }}
      //       />
      //     </div>
      //   ),
      // },
      {
        accessorKey: "title",
        header: ({ column }) => (
          <div
            onClick={() => handleSorting("title")}
            className="cursor-pointer flex gap-1 items-center"
          >
            Title
            {sortingColumn === "title" ? (
              sortingOrder === "asc" ? (
                <MoveUp size={14} />
              ) : (
                <MoveDown size={14} />
              )
            ) : (
              <ArrowUpDown size={14} />
            )}
          </div>
        ),
        size: 200,
        cell: ({ row }) => (
          <div className="flex break-words">
            <span className="capitalize overflow-hidden text-ellipsis mt-1">
              {row.original.title
                ? row.original.title.length > 80
                  ? `${row.original.title.substring(0, 50)}...`
                  : row.original.title
                : "No Title"}
              <br />
            </span>
          </div>
        ),
      },
      {
        accessorKey: "totalLikes",
        header: ({ column }) => (
          <div
            onClick={() => handleSorting("totalLikes")}
            className="cursor-pointer flex gap-1 items-center"
          >
            Likes
            {sortingColumn === "totalLikes" ? (
              sortingOrder === "asc" ? (
                <MoveUp size={14} />
              ) : (
                <MoveDown size={14} />
              )
            ) : (
              <ArrowUpDown size={14} />
            )}
          </div>
        ),
        cell: ({ cell }) => (
          <span>
            {(cell.getValue() as number)
              ? autoFormatNumber(cell.getValue() as number)
              : 0}
          </span>
        ),
        size: 100,
      },
      // dont remove this
      // {
      //   accessorKey: "totalDislikes",
      //   header: ({ column }) => (
      //     <div
      //       onClick={() => handleSorting("totalDislikes")}
      //       className="cursor-pointer flex gap-1 items-center"
      //     >
      //       Dislikes
      //       {sortingColumn === "totalDislikes" ? (
      //         sortingOrder === "asc" ? (
      //           <MoveUp size={14} />
      //         ) : (
      //           <MoveDown size={14} />
      //         )
      //       ) : (
      //         <ArrowUpDown size={14} />
      //       )}
      //     </div>
      //   ),
      //   cell: ({ cell }) => (
      //     <span>
      //       {(cell.getValue() as number) ? (cell.getValue() as number) : 0}
      //     </span>
      //   ),
      //   size: 100,
      // },
      {
        accessorKey: "totalViews",
        header: ({ column }) => (
          <div
            onClick={() => handleSorting("totalViews")}
            className="cursor-pointer flex gap-1 items-center"
          >
            Views
            {sortingColumn === "totalViews" ? (
              sortingOrder === "asc" ? (
                <MoveUp size={14} />
              ) : (
                <MoveDown size={14} />
              )
            ) : (
              <ArrowUpDown size={14} />
            )}
          </div>
        ),
        cell: ({ cell }) => (
          <span>
            {(cell.getValue() as number)
              ? autoFormatNumber(cell.getValue() as number)
              : 0}
          </span>
        ),
        size: 100,
      },
      {
        accessorKey: "createdAt",
        header: ({ column }) => (
          <div
            onClick={() => handleSorting("createdAt")}
            className="cursor-pointer flex gap-1 items-center"
          >
            Added On{" "}
            {sortingColumn === "createdAt" ? (
              sortingOrder === "asc" ? (
                <MoveUp size={14} />
              ) : (
                <MoveDown size={14} />
              )
            ) : (
              <ArrowUpDown size={14} />
            )}
          </div>
        ),
        cell: ({ cell }) => (
          <span>{formatDate(cell.getValue() as string, "dd MMM yyyy")}</span>
        ),
        size: 120,
      },
      {
        accessorKey: "videoStatus",
        header: ({ column }) => (
          <div
            onClick={() => handleSorting("videoStatus")}
            className="cursor-pointer flex gap-1 items-center"
          >
            Status{" "}
            {sortingColumn === "videoStatus" ? (
              sortingOrder === "asc" ? (
                <MoveUp size={14} />
              ) : (
                <MoveDown size={14} />
              )
            ) : (
              <ArrowUpDown size={14} />
            )}
          </div>
        ),
        cell: ({ cell }) => (
          <span
            className={`p-2 py-1 rounded-md text-xs ${
              cell.getValue() === "In-Process"
                ? "bg-blue-200 text-blue-700"
                : cell.getValue() === "Published"
                ? "bg-customGreenLight text-customGreenColor"
                : ""
            }`}
          >
            {cell.getValue() as string}
          </span>
        ),
        size: 120,
        meta: {
          isTooltip: false,
        },
      },
      {
        header: "Action",
        cell: ({ row }) => (
          <div className="flex gap-2 justify-center">
            <button
              onClick={() => {
                dispatch(setAppState({ routeName: "edit Video" }));
                handleEditvideo(row.original.id);
              }}
            >
              <MdModeEdit className="h-4 w-4 text-customGrayText hover:text-black" />
            </button>

            <button
              onClick={() =>
                handleRemoveVideo(row.original.id, row.original.statusId)
              }
              disabled={deletingVideoId === row.original.id}
            >
              {deletingVideoId === row.original.id ? (
                <div className="loader" />
              ) : (
                <FaTrashAlt className="text-customGrayText hover:text-black" />
              )}
            </button>
          </div>
        ),
        size: 75,
        meta: {
          isTooltip: false,
        },
      },
    ];

    return baseColumns;
  }, [sortingColumn, sortingOrder, deletingVideoId]);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    pageCount: Math.ceil(totalCount / pageSize),
    manualPagination: true,
    state: {
      pagination: {
        pageIndex: pageNumber - 1,
        pageSize,
      },
    },
  });

  function handleVendorChange(vendorId: string): void {
    setSelectedVendor(vendorId);
    setPageNumber(1);
  }

  return (
    <div className="overflow-hidden pr-2 h-full mr-4">
      <div className="flex flex-col md:flex-row justify-end items-start md:items-center mb-4 gap-2">
        <div className="flex flex-col md:flex-row gap-2 w-full md:w-auto items-stretch md:items-center">
          <Button
            onClick={() => {
              navigate("/add-video");
            }}
            className="bg-customGreenColor text-white text-sm px-4 h-9 rounded-lg hover:bg-customGreenColor w-full md:w-auto order-1"
          >
            Add Video
          </Button>

          {userRole === "ADMIN" && (
            <VendorsListDropdown
              userRole={decodedToken.role}
              className="w-full md:w-48 order-2"
              onVendorChange={handleVendorChange}
              selectedVendorId={selectedVendor}
            />
          )}

          <div className="relative flex justify-center text-customGrayText items-center w-full md:w-48 order-3">
            <FiSearch className="absolute left-2 text-lg" />
            <Input
              placeholder="Search video by title"
              className="text-sm text-left h-9 pl-8 w-full"
              onChange={(e: any) => {
                setPageNumber(1);
                setSearchTerm(e.target.value);
              }}
            />
          </div>
          <div className="order-4">
            <SingleDatePicker
              startDate={startDate}
              endDate={endDate}
              onStartDateSelect={setStartDate}
              onEndDateSelect={setEndDate}
            />
          </div>
        </div>
      </div>
      {getVideosLoader ? (
        <div className="overflow-y-auto h-[calc(100vh-12rem)]">
          <div className="w-full rounded-lg shadow bg-white h-full">
            <Table>
              <TableHeader className="bg-customGreenColor text-white">
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <TableCell
                        key={header.id}
                        className={`font-medium text-sm text-nowrap`}
                        style={{
                          minWidth: header.column.columnDef.size,
                          maxWidth: header.column.columnDef.size,
                        }}
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableHeader>
            </Table>
            <SkeletonTableLoading />
          </div>
        </div>
      ) : error ? (
        <div>Error loading videos.</div>
      ) : (
        <>
          <div className="overflow-y-auto h-[calc(100vh-12rem)]">
            <div className="overflow-x-auto w-full rounded-lg shadow bg-white h-full">
              <Table>
                <TableHeader className="bg-customGreenColor text-white sticky top-0">
                  {table.getHeaderGroups().map((headerGroup) => (
                    <TableRow key={headerGroup.id}>
                      {headerGroup.headers.map((header) => (
                        <TableHead
                          key={header.id}
                          className={`font-medium text-sm text-nowrap`}
                          style={{
                            minWidth: header.column.columnDef.size,
                            maxWidth: header.column.columnDef.size,
                          }}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                        </TableHead>
                      ))}
                    </TableRow>
                  ))}
                </TableHeader>
                <TableBody>
                  {data.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={columns.length}
                        className="text-center"
                      >
                        No data available
                      </TableCell>
                    </TableRow>
                  ) : (
                    table.getRowModel().rows.map((row) => (
                      <TableRow key={row.id}>
                        {row.getVisibleCells().map((cell) => (
                          <TableCell
                            key={cell.id}
                            className={`font-medium text-sm whitespace-nowrap overflow-hidden`}
                            style={{
                              minWidth: cell.column.columnDef.size,
                              maxWidth: cell.column.columnDef.size,
                            }}
                            isTooltip={
                              cell.column.columnDef.meta?.isTooltip ?? true
                            }
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
          {data.length > 0 && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-2 pt-4 pb-4 overflow-hidden">
              <div className="text-[#667085]">
                Page {table.getState().pagination.pageIndex + 1} of{" "}
                {Math.ceil(totalCount / pageSize)}
              </div>

              <Pagination
                totalCount={totalCount}
                pageSize={pageSize}
                currentPage={pageNumber}
                onPageChange={handlePageChange}
              />
            </div>
          )}
          {/* <ShareButton
                        setIsDialogOpen={setIsDialogOpen}
                        isDialogOpen={isDialogOpen}
                        title={`Share Your Video Link`}
                        text={`Share your video with friends and customers! Copy the link below and send it to them via email, chat, or social media to showcase your video and grow your business.`}
                        url={`${DEEP_LINK_BASE_URL}/Main/HomeStack/videoDetailContainer?id=${selectedvideoId}`}
                    /> */}
        </>
      )}
      <VideoPreviewModal
        videoSrc={selectedVideoPath}
        isOpen={isPreviewOpen}
        onClose={() => {
          console.log("Closing preview modal");
          setIsPreviewOpen(false);
          setSelectedVideoPath("");
        }}
        fileType={FILE_TYPE_ID.VIDEO}
      />
    </div>
  );
};

export default VideoListView;
